/**
 * TS-5.12 / TS-5.13 — billability adapter.
 *
 * The pure `timeEntryBillability` engine (sibling session) operates on
 * its own normalized `ContractType` + `ChargeCodeCategory` taxonomies.
 * The legacy timesheet types use shorter codes (`TsContract.type` =
 * 'CPFF' | 'FFP' | 'T&M' | ..., `TsChargeCode.pool` = IndirectPool | null).
 *
 * This adapter bridges the two so UI surfaces (WeekGridView,
 * MyTimesheetPage) can call the engine without rewriting the chain.
 */
import type { TsChargeCode, TsContract } from '../types/timesheet.types'
import {
  resolveBillableFlag,
  type BillableFlagResolution,
  type ChargeCodeCategory,
  type ContractType,
} from './timeEntryBillability'

/**
 * Map a TsContract.type string to the engine's ContractType taxonomy.
 * Unknown strings fall through to 'unknown' so the resolver returns a
 * safe default ("not billable, no customer") without throwing.
 */
export function mapTsContractTypeToBillability(
  type: TsContract['type'] | string | undefined,
): ContractType {
  switch (type) {
    case 'CPFF':
      return 'cost_plus_fixed_fee'
    case 'CPIF':
      // CPIF (cost-plus-incentive-fee) shares the cost-plus billing
      // behavior with CPAF — both bill at actuals with a variable fee.
      return 'cost_plus_award_fee'
    case 'FFP':
      return 'firm_fixed_price'
    case 'T&M':
      return 'time_and_materials'
    case 'IDIQ':
      return 'idiq'
    case 'Indirect':
      return 'internal'
    default:
      return 'unknown'
  }
}

/**
 * Derive a ChargeCodeCategory from a TsChargeCode by inspecting its
 * `pool` field. When pool is null/undefined and the charge code has a
 * contractId, the code is treated as `direct_project`. Otherwise the
 * pool drives the category (G&A / Overhead / B&P / IR&D / Fringe).
 */
export function chargeCodeCategoryFromTsChargeCode(
  cc: TsChargeCode,
): ChargeCodeCategory {
  if (!cc.pool) return 'direct_project'
  switch (cc.pool) {
    case 'G&A':
      return 'g_and_a'
    case 'OH':
      return 'overhead'
    case 'B&P':
      return 'b_and_p'
    case 'IR&D':
      return 'ird'
    case 'Fringe':
      return 'fringe'
    default:
      // Unrecognized pool falls through to indirect bucket.
      return 'indirect'
  }
}

/**
 * Convenience composition for the entry-row surfaces: given a TsChargeCode
 * + its TsContract (when available), resolve the suggested billable flag
 * + customer reference. Caller passes the resolution into
 * `<EntryRowDetailsPopover suggestion={...} />`.
 */
export function resolveRowBillableSuggestion(
  cc: TsChargeCode,
  contract: TsContract | undefined,
): BillableFlagResolution {
  const category = chargeCodeCategoryFromTsChargeCode(cc)
  return resolveBillableFlag({
    chargeCodeCategory: category,
    contract: contract
      ? {
          contractType: mapTsContractTypeToBillability(contract.type),
          // TsContract has agency (e.g. "Boeing", "Gentex Aerospace");
          // use it as the customer reference for the entry-row popover.
          // When the customers table lands the resolver will hydrate
          // from a real foreign key.
          customerId: contract.agency,
        }
      : undefined,
  })
}

/**
 * Billability is a property of the CHARGE LINE, not a per-entry user choice.
 * A charge line is billable (and to which customer) based on its explicit
 * `billable` flag, falling back to the derived suggestion (charge-code category
 * + contract type). Used for the grid's BILL badge AND to stamp every saved
 * entry on persist, so the worker never sets billability themselves.
 */
export function resolveChargeLineBillable(
  cc: TsChargeCode,
  contract: TsContract | undefined,
): { isBillable: boolean; customerId: string | null } {
  const suggestion = resolveRowBillableSuggestion(cc, contract)
  const isBillable = cc.billable ?? suggestion.isBillable
  return { isBillable, customerId: isBillable ? suggestion.customerId : null }
}
